import { useState, useRef, useEffect } from 'react';
import { PencilFullIcon } from '../../../icons/SvgIcons';
import { PrimaryButton, WhiteButton } from '../Button/Buttons';
import TextArea from '../TextArea/TextArea';

const OPTION_FOCUS_SHADOW =
  '0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px 0 rgba(201, 200, 200, 0.06), 0 0 0 2px #4338CA, 0 0 0 4px #FFF';

const LINE_HEIGHT = 20;
const MAX_ROWS = 4;
const MAX_HEIGHT = LINE_HEIGHT * MAX_ROWS;

type UserAnswerProps = {
  userAnswer: string;
  isEditable?: boolean;
  isDisabled?: boolean;
  isEditActive?: boolean;
  options?: string[];
  onUpdate?: (newAnswer: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
};

export const UserAnswer = ({
  userAnswer,
  isEditable = true,
  isDisabled = false,
  isEditActive = false,
  options = [],
  onUpdate,
  onEditStart,
  onEditEnd
}: UserAnswerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(userAnswer);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [focusedOption, setFocusedOption] = useState<string | null>(null);
  const [hoveredEditButton, setHoveredEditButton] = useState(false);
  const pRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (pRef.current) {
      setIsTruncated(pRef.current.scrollHeight > MAX_HEIGHT);
    }
  }, [userAnswer]);

  useEffect(() => {
    if (!isEditActive && isEditing) {
      setIsEditing(false);
      setDraft(userAnswer);
      setSelectedOption(null);
      setFocusedOption(null);
    }
  }, [isEditActive, isEditing, userAnswer]);

  const hasChanged = draft !== userAnswer;
  const hasValidDraft = draft.trim() !== '';
  const shouldShowOptionsEditor = options.some(
    (option) => option.trim() === userAnswer.trim()
  );

  const handleEditClick = () => {
    setDraft(userAnswer);
    setSelectedOption(null);
    setFocusedOption(null);
    setIsEditing(true);
    onEditStart?.();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraft(userAnswer);
    setSelectedOption(null);
    setFocusedOption(null);
    onEditEnd?.();
  };

  const handleUpdate = () => {
    if (!hasChanged || !hasValidDraft) {
      return;
    }

    onUpdate?.(draft);
    setIsEditing(false);
    onEditEnd?.();
  };

  const handleOptionUpdate = (option: string) => {
    setSelectedOption(option);
    onUpdate?.(option);
    setDraft(option);
    setIsEditing(false);
    setFocusedOption(null);
    onEditEnd?.();
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className={`relative p-4 rounded-[8px] ${
          isEditing ? '' : 'pr-12  bg-gray-25'
        }`}
      >
        {isEditable && !isEditing && (
          <button
            className="p-1 outline-none border-none shadow-none w-fit absolute top-2 right-2 rounded-full hover:bg-gray-100"
            disabled={isDisabled}
            onClick={handleEditClick}
            onMouseOver={() => setHoveredEditButton(true)}
            onMouseOut={() => setHoveredEditButton(false)}
            title="Editing this answer will recalculate subsequent questions"
          >
            {PencilFullIcon(hoveredEditButton ? '#606169' : '#9CA3AF')}
          </button>
        )}

        {isEditing && shouldShowOptionsEditor ? (
          <div className="flex flex-wrap gap-2">
            {options.map((option) => {
              const isActive = selectedOption === option;

              return (
                <button
                  key={option}
                  className={`py-[17px] px-[24px] h-[54px] flex items-center rounded-lg border transition-colors focus:outline-none font-medium ${
                    isActive
                      ? 'ring-2 ring-purple-400 bg-blue-50 text-purple-900'
                      : 'border ring-gray-300 hover:ring-gray-400 text-gray-900'
                  }`}
                  style={{
                    boxShadow:
                      focusedOption === option ? OPTION_FOCUS_SHADOW : undefined
                  }}
                  onClick={() => handleOptionUpdate(option)}
                  onFocus={() => setFocusedOption(option)}
                  onBlur={() => setFocusedOption(null)}
                >
                  <span className="text-sm">{option}</span>
                </button>
              );
            })}
          </div>
        ) : isEditing ? (
          <TextArea value={draft} onChange={setDraft} rows={4} />
        ) : (
          <>
            <p
              ref={pRef}
              className="text-sm text-gray-900 overflow-hidden"
              style={{ maxHeight: isExpanded ? 'none' : `${MAX_HEIGHT}px` }}
            >
              {userAnswer}
            </p>

            {isTruncated && (
              <button
                className="mt-1 text-xs text-purple-800 hover:text-purple-900 font-medium focus:outline-none"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded ? 'Show Less' : 'Show More'}
              </button>
            )}
          </>
        )}
      </div>

      {isEditing && (
        <div className="flex gap-1 justify-end">
          <WhiteButton size="xs" onClick={handleCancel}>
            Cancel
          </WhiteButton>
          {!shouldShowOptionsEditor && (
            <PrimaryButton
              size="xs"
              onClick={handleUpdate}
              disabled={!hasChanged || !hasValidDraft}
            >
              Update
            </PrimaryButton>
          )}
        </div>
      )}
    </div>
  );
};
